import { type MutableRefObject, useCallback, useEffect, useRef } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import {
  canBringSelectionForward,
  canSendSelectionBackward,
  liveLayerOrder,
} from '@/lib/layout/layer-order'

/**
 * Bring-forward / send-backward availability against the unified (shape + text) layer order,
 * plus the latest-value mirror of that order other canvas code reads without re-subscribing.
 */
export function useLayerArrangeGates({
  unifiedLayerIds,
  internalShapeManagerRef,
}: {
  unifiedLayerIds: string[]
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
}) {
  // Mirror unifiedLayerIds in a ref so arrange can-checks always read the latest value
  // without needing to be recreated on every render (keeps the keydown effect deps stable).
  const unifiedLayerIdsRef = useRef(unifiedLayerIds)

  useEffect(() => {
    unifiedLayerIdsRef.current = unifiedLayerIds
  }, [unifiedLayerIds])

  // Only ids still on the canvas count (see `liveLayerOrder`, WMPCM-12998).
  const liveLayerIds = useCallback(
    (): string[] =>
      liveLayerOrder(unifiedLayerIdsRef.current, internalShapeManagerRef.current?.getShapes()),
    [internalShapeManagerRef]
  )

  const canUnifiedBringForward = useCallback(
    (selectedIds: string[]): boolean => canBringSelectionForward(selectedIds, liveLayerIds()),
    [liveLayerIds]
  )

  const canUnifiedSendBackward = useCallback(
    (selectedIds: string[]): boolean => canSendSelectionBackward(selectedIds, liveLayerIds()),
    [liveLayerIds]
  )

  // Compute selected IDs for arrange can-checks at a given callsite.
  const getArrangeSelectedIds = useCallback(
    (): string[] => internalShapeManagerRef.current?.getSelectedShapeIds() ?? [],
    [internalShapeManagerRef]
  )

  return {
    unifiedLayerIdsRef,
    canUnifiedBringForward,
    canUnifiedSendBackward,
    getArrangeSelectedIds,
  }
}
